"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import type {
  HeroSectionData,
  SectionProps,
} from "@/themes/default/templates/section/types";
import { getLocalizedText } from "@/themes/default/templates/section/utils";

/**
 * Crystal Hero (classic) — the company-profile mockup hero.
 *
 * Two-column over the crystal-night aurora: thin prism headline + lead +
 * glass CTAs + pillar tags on the LEFT; a slow-drifting crystal-facet
 * pattern on the RIGHT. When the section carries background photos it
 * cross-fades them under a deep scrim and the facet pattern steps aside.
 *
 * Pillar tags come from `section.pills` — an array of
 * `{ text: MultiLanguageText, color?: 'indigo'|'lime'|'coral'|'azure' }`.
 */

const PILL_COLORS: Record<string, string> = {
  indigo: "var(--crystal-indigo)",
  lime: "var(--crystal-lime)",
  coral: "var(--crystal-coral)",
  azure: "var(--crystal-azure)",
};

function toUrl(v: unknown): string | undefined {
  if (!v) return undefined;
  if (typeof v === "string") return v;
  if (typeof v === "object") {
    const o = v as Record<string, any>;
    return o.url || o.image || o.src || o.backgroundImage || undefined;
  }
  return undefined;
}

function collectImages(section: HeroSectionData): string[] {
  const s = section as Record<string, any>;
  const out: string[] = [];
  const push = (v: unknown) => {
    const u = toUrl(v);
    if (u) out.push(u);
  };
  if (Array.isArray(s.images)) s.images.forEach(push);
  if (Array.isArray(s.slides))
    s.slides.forEach((sl: any) => push(sl?.backgroundImage ?? sl?.image ?? sl));
  if (out.length === 0) {
    push(section.backgroundImage);
    push(s.image);
    push(s.content?.image);
  }
  return Array.from(new Set(out));
}

function splitHeadline(text: string): { lead: string; accent: string } {
  const trimmed = text.trim();
  if (!trimmed) return { lead: "", accent: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { lead: "", accent: trimmed };
  return { lead: parts.slice(0, -1).join(" "), accent: parts[parts.length - 1] };
}

/** Slow-drifting crystal facet line-art (the brand prism, refracting). */
function FacetPattern() {
  return (
    <svg
      viewBox="0 0 520 520"
      className="crystal-hero-facets w-full"
      aria-hidden="true"
    >
      <g fill="none" stroke="var(--color-primary)" strokeWidth="1" opacity="0.45">
        <polygon points="260,40 470,220 260,300 60,220" />
        <polygon points="260,300 470,220 400,430 120,430 60,220" />
        <polygon points="260,40 260,300 120,430" />
        <polygon points="260,40 260,300 400,430" />
        <polygon points="60,220 260,300 120,430" />
        <polygon points="470,220 260,300 400,430" />
      </g>
      <polygon points="260,40 470,220 260,300 60,220" fill="var(--crystal-indigo)" opacity="0.07" />
      <polygon points="260,300 470,220 400,430 120,430 60,220" fill="var(--crystal-lime)" opacity="0.06" />
      <polygon points="260,40 260,300 120,430" fill="var(--crystal-coral)" opacity="0.05" />
    </svg>
  );
}

export function HeroSection({
  section,
  currentLanguage = "en",
}: SectionProps<HeroSectionData>) {
  const s = section as any;
  const headline = section.headline
    ? getLocalizedText(section.headline, currentLanguage)
    : s.content?.title
      ? getLocalizedText(s.content.title, currentLanguage)
      : "";
  const subhead = section.subheadline
    ? getLocalizedText(section.subheadline, currentLanguage)
    : s.content?.description
      ? getLocalizedText(s.content.description, currentLanguage)
      : "";
  const eyebrow = section.title
    ? getLocalizedText(section.title, currentLanguage)
    : s.content?.subtitle
      ? getLocalizedText(s.content.subtitle, currentLanguage)
      : "";

  const buttons: any[] = Array.isArray(section.buttons)
    ? section.buttons
    : [s.content?.primaryButton, s.content?.secondaryButton].filter(Boolean);

  const pills: any[] = Array.isArray(s.pills) ? s.pills : [];

  const images = collectImages(section);
  const hasImages = images.length > 0;

  const [active, setActive] = useState(0);
  useEffect(() => {
    if (images.length <= 1) return;
    const id = setInterval(
      () => setActive((i) => (i + 1) % images.length),
      6000,
    );
    return () => clearInterval(id);
  }, [images.length]);

  const heightKey = (s.height as string) || "large";
  const minHeight =
    heightKey === "fullscreen"
      ? "calc(100dvh - var(--site-header-h, 130px))"
      : heightKey === "small"
        ? "46vh"
        : heightKey === "medium"
          ? "60vh"
          : "76vh";

  const { lead, accent } = splitHeadline(headline);

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ minHeight, borderBottom: "1px solid var(--color-border)" }}
      data-section-id={section._id}
      data-section-type={section.type}
      data-hero-variant="classic"
    >
      {/* Background slides (cross-fade) when the hero carries photos. */}
      {images.map((src, i) => (
        <div
          key={`${src}-${i}`}
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{
            backgroundImage: `url(${src})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: i === active ? 1 : 0,
          }}
        />
      ))}
      {hasImages && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(7,12,23,0.55) 0%, rgba(7,12,23,0.82) 100%)",
          }}
          aria-hidden="true"
        />
      )}

      {/* Faint prism halo. */}
      <div
        className="absolute inset-x-0 top-0 h-2/3 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 70% at 50% 0%, rgba(124,147,245,0.16), transparent 70%)",
        }}
        aria-hidden="true"
      />

      {/* Crystal-signature facet backdrop — a low-opacity layer BEHIND the
          content, anchored to the right edge (near the scrollbar). */}
      {!hasImages && (
        <div
          className="pointer-events-none absolute right-0 top-1/2 hidden w-[560px] -translate-y-1/2 translate-x-1/4 opacity-40 lg:block"
          aria-hidden="true"
        >
          <FacetPattern />
        </div>
      )}

      {/* Content — centered. */}
      <div
        className="relative z-10 mx-auto flex max-w-4xl flex-col items-center justify-center px-4 py-20 text-center sm:px-6 md:py-28 lg:px-8"
        style={{ minHeight }}
      >
        {eyebrow && <span className="crystal-eyebrow mb-5 block">{eyebrow}</span>}
        {headline && (
          <h1
            className="crystal-display text-4xl sm:text-5xl lg:text-7xl"
            style={{ color: "var(--color-foreground)", textWrap: "balance" }}
          >
            {lead ? (
              <>
                <span className="block font-extralight">{lead}</span>
                <span className="block crystal-prism-text font-medium">
                  {accent}
                </span>
              </>
            ) : (
              <span className="crystal-prism-text font-medium">{accent}</span>
            )}
          </h1>
        )}
        {subhead && (
          <p
            className="mx-auto mt-7 max-w-2xl text-base leading-relaxed md:text-lg"
            style={{ color: "var(--color-muted-foreground)" }}
          >
            {subhead}
          </p>
        )}
        {buttons.length > 0 && (
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            {buttons.map((button, index) => {
              const isPrimary = (button.style ?? "primary") === "primary";
              return (
                <Link
                  key={index}
                  href={button.url || "#"}
                  target={button.openInNewTab ? "_blank" : undefined}
                  rel={button.openInNewTab ? "noopener noreferrer" : undefined}
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5 md:text-base"
                  style={
                    isPrimary
                      ? {
                          backgroundColor: "var(--color-primary)",
                          color: "var(--color-primary-foreground)",
                        }
                      : {
                          background: "rgba(255,255,255,0.06)",
                          color: "var(--color-foreground)",
                          border: "1px solid var(--color-border)",
                          backdropFilter: "blur(6px)",
                          WebkitBackdropFilter: "blur(6px)",
                        }
                  }
                >
                  {getLocalizedText(button.text, currentLanguage)}
                </Link>
              );
            })}
          </div>
        )}

        {/* Pillar tags — single line, centered (scrolls on very narrow screens). */}
        {pills.length > 0 && (
          <div className="mt-11 flex max-w-full flex-nowrap items-center justify-center gap-x-7 overflow-x-auto">
            {pills.map((pill, i) => (
              <span key={i} className="crystal-pill whitespace-nowrap">
                <i
                  style={{
                    background:
                      PILL_COLORS[pill.color as string] ||
                      "var(--crystal-indigo)",
                  }}
                />
                {getLocalizedText(pill.text ?? pill.label, currentLanguage)}
              </span>
            ))}
          </div>
        )}

        {/* Slider dots. */}
        {images.length > 1 && (
          <div className="mt-10 flex justify-center gap-2.5">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === active}
                className="h-2.5 rounded-full transition-all"
                style={{
                  backgroundColor:
                    i === active
                      ? "var(--color-primary)"
                      : "rgba(234,241,253,0.35)",
                  width: i === active ? "1.7rem" : "0.625rem",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default HeroSection;
